/**
 * Contact Page
 * 
 * Contact information with map embed placeholder and contact form.
 * Includes Run Club promotional section.
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import SEO, { breadcrumbs } from '../components/common/SEO'
import PageHero from '../components/common/PageHero'
import WaveSeparator from '../components/common/WaveSeparator'
import { MapPin, Clock, Mail, Phone, Instagram } from 'lucide-react'
import { Link } from 'react-router-dom'
import { BOOKING_URL, SITE_URL, business, hours } from '../data/site'

const FORMSPREE_ENDPOINT = `https://formspree.io/f/${
  import.meta.env.VITE_FORMSPREE_ID || 'mdareroq'
}`

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          _subject: `New contact form message from ${formData.name}`,
        }),
      })

      if (response.ok) {
        setIsSubmitted(true)
        setFormData({ name: '', email: '', phone: '', message: '' })
      } else {
        const data = await response.json().catch(() => ({}))
        setError(
          data?.errors?.[0]?.message ||
            'Something went wrong sending your message. Please try again or email us directly.'
        )
      }
    } catch {
      setError(
        `Could not reach the server. Please check your connection or email us directly at ${business.email}.`
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <>
      <SEO
        title="Contact & Book — Yonge & Eglinton Toronto"
        description="Contact Recreo Rehab at 161 Eglinton Ave E, Toronto. Book massage, athletic therapy, or naturopathic care online, or call (416) 826-2497."
        path="/contact"
        keywords="contact Recreo Rehab, book massage therapy Toronto, Yonge and Eglinton RMT, Midtown Toronto massage appointment"
        structuredData={[
          breadcrumbs([
            { name: 'Home', path: '/' },
            { name: 'Contact', path: '/contact' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact Recreo Rehab',
            url: `${SITE_URL}/contact`,
            mainEntity: { '@id': `${SITE_URL}/#business` },
          },
        ]}
      />

      {/* Hero */}
      <div className="bg-cream-100">
        <PageHero 
          title="Contact Recreo Rehab"
          subtitle="We'd love to hear from you"
          size="md"
          texture="orange-hue"
        />
      </div>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-cream-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <span className="font-script text-brand-orange-500 italic text-lg block mb-3">
                Reach Out
              </span>
              <h2 className="font-display text-dark-700 text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.15] mb-10">
                Get in touch with our team
              </h2>

              <div className="space-y-8">
                {/* Location */}
                <div className="flex gap-5">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-10 h-10 bg-brand-orange-500/10 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-brand-orange-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display text-dark-700 text-xl font-medium mb-2">
                      Location
                    </h3>
                    <address className="font-sans text-dark-500 text-base not-italic leading-relaxed">
                      {business.street}<br />
                      {business.city}, {business.region}, {business.postalCode}
                    </address>
                    <p className="font-sans text-dark-400 text-xs mt-2 italic">
                      ({business.entranceNote})
                    </p>
                    <Link
                      to="/visit"
                      className="inline-block mt-2 font-sans text-sm text-brand-orange-600 underline underline-offset-4"
                    >
                      Directions &amp; parking →
                    </Link>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex gap-5">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-10 h-10 bg-brand-orange-500/10 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-brand-orange-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display text-dark-700 text-xl font-medium mb-2">
                      Hours
                    </h3>
                    <div className="font-sans text-dark-500 text-base leading-relaxed space-y-3">
                      {hours
                        .filter((h) => h.time !== 'Closed')
                        .map((h) => (
                          <p key={h.days}>
                            {h.days}
                            <br />
                            {h.time}
                          </p>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-5">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-10 h-10 bg-brand-orange-500/10 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-brand-orange-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display text-dark-700 text-xl font-medium mb-2">
                      Email
                    </h3>
                    <a 
                      href={`mailto:${business.email}`}
                      className="font-sans text-dark-500 text-base hover:text-brand-orange-500 transition-colors"
                    >
                      {business.email}
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-5">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-10 h-10 bg-brand-orange-500/10 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-brand-orange-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display text-dark-700 text-xl font-medium mb-2">
                      Phone
                    </h3>
                    <a 
                      href={business.phoneHref}
                      className="font-sans text-dark-500 text-base hover:text-brand-orange-500 transition-colors"
                    >
                      {business.phoneDisplay}
                    </a>
                  </div>
                </div>
              </div>

              {/* Book Online CTA */}
              <div className="mt-12">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-brand-orange-500 text-white px-8 py-3 rounded-full font-sans text-[11px] font-medium tracking-[0.15em] uppercase hover:bg-brand-orange-400 transition-colors duration-200 shadow-sm"
                >
                  Book Online
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
              className="bg-white rounded-2xl p-8 md:p-10 border border-dark-200/25 shadow-sm"
            >
              <h2 className="font-display text-dark-700 text-2xl md:text-3xl font-medium mb-8">
                Send a Message
              </h2>

              {isSubmitted ? (
                <div className="bg-brand-green-50/50 border border-brand-green-300/50 rounded-xl p-8 text-center">
                  <p className="font-sans text-brand-green-700 text-base leading-relaxed">
                    Thank you for your message! We'll be in touch shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block font-sans text-xs font-medium tracking-[0.1em] uppercase text-dark-500 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-dark-200/30 bg-cream-50 font-sans text-dark-700 text-base
                               focus:bg-white focus:outline-none focus:border-brand-orange-400 focus:ring-1 focus:ring-brand-orange-400
                               rounded-xl transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block font-sans text-xs font-medium tracking-[0.1em] uppercase text-dark-500 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-dark-200/30 bg-cream-50 font-sans text-dark-700 text-base
                               focus:bg-white focus:outline-none focus:border-brand-orange-400 focus:ring-1 focus:ring-brand-orange-400
                               rounded-xl transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block font-sans text-xs font-medium tracking-[0.1em] uppercase text-dark-500 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-dark-200/30 bg-cream-50 font-sans text-dark-700 text-base
                               focus:bg-white focus:outline-none focus:border-brand-orange-400 focus:ring-1 focus:ring-brand-orange-400
                               rounded-xl transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-sans text-xs font-medium tracking-[0.1em] uppercase text-dark-500 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-dark-200/30 bg-cream-50 font-sans text-dark-700 text-base
                               focus:bg-white focus:outline-none focus:border-brand-orange-400 focus:ring-1 focus:ring-brand-orange-400
                               rounded-xl transition-all resize-none"
                    />
                  </div>

                  {error && (
                    <p className="font-sans text-red-700 text-sm leading-relaxed">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-block bg-brand-green-600 text-white px-8 py-3.5 rounded-full font-sans text-[11px] font-medium tracking-[0.15em] uppercase hover:bg-brand-green-700 transition-colors duration-200 shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Run Club Section */}
      <section className="bg-cream-100 pb-20 md:pb-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="bg-brand-green-700 rounded-3xl p-10 md:p-16 text-center shadow-lg relative overflow-hidden"
          >
            {/* Subtle noise texture overlay */}
            <div
              className="absolute inset-0 opacity-20 pointer-events-none"
              style={{
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
              }}
            />
            
            <div className="relative z-10">
              <span className="font-script text-brand-orange-500 italic text-lg block mb-4">
                community
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white font-medium mb-6">
                Join Our Run Club
              </h2>
              <p className="font-sans text-cream-200/90 text-sm md:text-base leading-relaxed mb-10 max-w-2xl mx-auto">
                Connect with the Recreo community! Join us for weekly group runs and stay 
                updated on upcoming events, wellness tips, and recovery strategies.
              </p>
              <a
                href="https://instagram.com/recreorehab"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-dark-700 px-8 py-3.5 rounded-full font-sans text-[11px] font-medium tracking-[0.15em] uppercase hover:bg-cream-100 transition-colors shadow-sm"
              >
                <Instagram className="w-4 h-4 mb-[1px]" />
                Follow on Instagram
              </a>
            </div>
          </motion.div>
        </div>
      </section>

    </>
  )
}

export default ContactPage
